import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  CAlert,
  CButton,
  CCard,
  CCardBody,
  CCol,
  CFormInput,
  CInputGroup,
  CRow,
} from '@coreui/react-pro'
import CIcon from '@coreui/icons-react'
import {
  cilCheckCircle,
  cilGift,
  cilPlus,
  cilReload,
  cilSearch,
  cilStar,
  cilWarning,
} from '@coreui/icons'
import Table from './Table'
import AddForm from './AddForm'
import EditForm from './EditForm'
import GamePrizeService from 'src/service/GamePrizeService'
import './GamePrize.css'

const GamePrize = () => {
  const [users, setUsers] = useState({})
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const [activePage, setActivePage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [columnSorter, setColumnSorter] = useState({})
  const [searchInput, setSearchInput] = useState('')
  const [nameFilter, setNameFilter] = useState('')
  const [addVisible, setAddVisible] = useState(false)
  const [editPrizeId, setEditPrizeId] = useState(null)

  const fetchUsers = useCallback(async () => {
    setLoading(true)
    setError(false)
    const offset = itemsPerPage * activePage - itemsPerPage
    const params = new URLSearchParams()
    if (nameFilter) params.append('name', nameFilter)
    if (columnSorter && columnSorter.column !== undefined) {
      params.append('sort', `${columnSorter.column}%${columnSorter.state}`)
    }

    try {
      const result = await GamePrizeService.getPrize(offset, itemsPerPage, activePage, params)
      setUsers(result.data)
    } catch (e) {
      setError(true)
    } finally {
      setLoading(false)
    }
  }, [activePage, itemsPerPage, columnSorter, nameFilter])

  useEffect(() => {
    fetchUsers()
  }, [fetchUsers])

  // Debounce the search box, same as the other refreshed listings.
  useEffect(() => {
    const timer = setTimeout(() => {
      const term = searchInput.trim()
      if (nameFilter === term) return
      setActivePage(1)
      setNameFilter(term)
    }, 350)
    return () => clearTimeout(timer)
  }, [searchInput, nameFilter])

  const { activeCount, featuredCount } = useMemo(() => {
    const prizes = users?.data || []
    return {
      activeCount: prizes.filter((p) => p.is_active === 1).length,
      featuredCount: prizes.filter((p) => p.is_featured === 1).length,
    }
  }, [users])

  return (
    <>
      <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
        <div className="ge-page-header mb-0">
          <h1 className="ge-page-title">Game Prizes</h1>
          <p className="ge-page-subtitle">Create and manage the prizes members can win.</p>
        </div>
        <CButton color="primary" onClick={() => setAddVisible(true)}>
          <CIcon icon={cilPlus} className="me-1" />
          Add Prize
        </CButton>
      </div>

      {error && (
        <CAlert color="danger" className="d-flex flex-wrap align-items-center gap-2">
          <CIcon icon={cilWarning} className="flex-shrink-0" />
          <span>We couldn&apos;t load your prizes.</span>
          <CButton
            color="danger"
            variant="outline"
            size="sm"
            className="ms-auto"
            onClick={fetchUsers}
          >
            <CIcon icon={cilReload} className="me-1" />
            Retry
          </CButton>
        </CAlert>
      )}

      <CRow className="g-3 mb-3">
        <CCol sm={6} lg={4}>
          <CCard className="gp-stat-card h-100">
            <CCardBody className="d-flex align-items-center gap-3">
              <span className="gp-stat-icon gp-stat-icon--primary">
                <CIcon icon={cilGift} size="lg" />
              </span>
              <div>
                <div className="gp-stat-label">Total Prizes</div>
                <div className="gp-stat-value">{users?.total ?? 0}</div>
              </div>
            </CCardBody>
          </CCard>
        </CCol>
        <CCol sm={6} lg={4}>
          <CCard className="gp-stat-card h-100">
            <CCardBody className="d-flex align-items-center gap-3">
              <span className="gp-stat-icon gp-stat-icon--success">
                <CIcon icon={cilCheckCircle} size="lg" />
              </span>
              <div>
                <div className="gp-stat-label">Active on This Page</div>
                <div className="gp-stat-value">{activeCount}</div>
              </div>
            </CCardBody>
          </CCard>
        </CCol>
        <CCol sm={6} lg={4}>
          <CCard className="gp-stat-card h-100">
            <CCardBody className="d-flex align-items-center gap-3">
              <span className="gp-stat-icon gp-stat-icon--warning">
                <CIcon icon={cilStar} size="lg" />
              </span>
              <div>
                <div className="gp-stat-label">Featured on This Page</div>
                <div className="gp-stat-value">{featuredCount}</div>
              </div>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>

      <CCard>
        <CCardBody>
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
            <CInputGroup className="ge-search">
              <span className="ge-search-icon">
                <CIcon icon={cilSearch} />
              </span>
              <CFormInput
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search by prize name..."
                aria-label="Search prizes by name"
              />
            </CInputGroup>
          </div>

          <Table
            users={users}
            fetchUsers={fetchUsers}
            setActivePage={setActivePage}
            setItemsPerPage={setItemsPerPage}
            setColumnSorter={setColumnSorter}
            itemsPerPage={itemsPerPage}
            activePage={activePage}
            columnSorter={columnSorter}
            loading={loading}
            onEditPrize={setEditPrizeId}
          />
        </CCardBody>
      </CCard>

      <AddForm visible={addVisible} onClose={() => setAddVisible(false)} fetchUsers={fetchUsers} />

      <EditForm
        prizeId={editPrizeId}
        visible={!!editPrizeId}
        onClose={() => setEditPrizeId(null)}
        fetchUsers={fetchUsers}
      />
    </>
  )
}

export default GamePrize
